<template>
  <b-modal
    :id="id"
    title="Gerar Quitação de Títulos (DQ)"
    size="lg"
    no-close-on-backdrop
    @ok.prevent="confirmarDQ"
  >
    <div class="p-1">
      <h5 class="mb-1">
        Títulos Selecionados ({{ selectedRows.length }})
      </h5>
      <b-table
        :items="selectedRows"
        :fields="fieldsTable"
        small
        responsive
        striped
      >
        <template #cell(dataVencimento)="data">
          {{ formatDateGrid(data.value) }}
        </template>
        <template #cell(valorTotal)="data">
          {{ formatCurrencyBrazil(data.value) }}
        </template>
      </b-table>

      <b-row class="mt-2">
        <b-col md="6">
          <b-form-group
            label="Conta Bancária / Caixa"
            label-for="dq-conta"
          >
            <b-form-select
              id="dq-conta"
              v-model="dqModal.idConta"
              :options="comboContas"
              value-field="id"
              text-field="descricao"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Data de Quitação"
            label-for="dq-data"
          >
            <b-form-input
              id="dq-data"
              v-model="dqModal.dataQuitacao"
              type="date"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="bg-light p-1 rounded mb-2">
        <b-col md="4 text-center">
          <small class="text-muted d-block">Subtotal Nominal</small>
          <h4 class="font-weight-bold">
            {{ formatCurrencyBrazil(dqModal.totalNominal) }}
          </h4>
        </b-col>
        <b-col md="4">
          <b-form-group
            label="Desconto Especial (R$)"
            label-for="dq-desconto"
          >
            <b-form-input
              id="dq-desconto"
              v-model.number="dqModal.desconto"
              type="number"
              step="0.01"
              @input="atualizarTotaisDQ"
            />
          </b-form-group>
        </b-col>
        <b-col md="4 text-center">
          <small class="text-muted d-block text-success">TOTAL A RECEBER</small>
          <h3 class="font-weight-bolder text-success">
            {{ formatCurrencyBrazil(dqModal.totalGeral) }}
          </h3>
        </b-col>
      </b-row>

      <h5 class="mb-1 d-flex justify-content-between">
        Formas de Pagamento
        <b-button
          variant="outline-primary"
          size="sm"
          @click="addPagamentoDQ"
        >
          + Adicionar Pagamento
        </b-button>
      </h5>

      <b-row
        v-for="(pag, index) in dqModal.pagamentos"
        :key="index"
        class="mb-1 align-items-center"
      >
        <b-col md="5">
          <b-form-select
            v-model="pag.idFormaPagamento"
            :options="comboFormaPagamento"
            value-field="id"
            text-field="nome"
            placeholder="Forma de Pagamento"
          />
        </b-col>
        <b-col md="5">
          <b-input-group prepend="R$">
            <b-form-input
              v-model.number="pag.valor"
              type="number"
              step="0.01"
            />
          </b-input-group>
        </b-col>
        <b-col md="2">
          <b-button
            variant="flat-danger"
            class="btn-icon"
            :disabled="dqModal.pagamentos.length === 1"
            @click="removePagamentoDQ(index)"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </b-col>
      </b-row>

      <div
        v-if="dqModal.totalGeral > 0"
        class="mt-1"
      >
        <b-alert
          show
          :variant="Math.abs(somaPagamentos - dqModal.totalGeral) < 0.01 ? 'success' : 'warning'"
        >
          Soma dos pagamentos: <strong>{{ formatCurrencyBrazil(somaPagamentos) }}</strong>
        </b-alert>
      </div>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="outline-secondary"
        @click="$bvModal.hide(id)"
      >
        Cancelar
      </b-button>
      <b-button
        variant="success"
        :disabled="Math.abs(somaPagamentos - dqModal.totalGeral) > 0.01 || !dqModal.idConta"
        @click="confirmarDQ"
      >
        Finalizar Quitação
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BRow, BCol, BButton, BFormGroup, BFormInput, BFormSelect, BModal, BInputGroup, BTable, BAlert,
} from 'bootstrap-vue'
import mixinsGlobal from '@/mixins'

export default {
  name: 'ModalGerarQuitacaoDQ',
  components: {
    BRow, BCol, BButton, BFormGroup, BFormInput, BFormSelect, BModal, BInputGroup, BTable, BAlert,
  },
  mixins: [mixinsGlobal],
  props: {
    id: { type: String, default: 'modal-gerar-quitacao-dq' },
    selectedRows: { type: Array, required: true },
    comboContas: { type: Array, required: true },
    comboFormaPagamento: { type: Array, required: true },
  },
  data() {
    return {
      dqModal: {
        totalNominal: 0,
        totalEncargos: 0,
        totalGeral: 0,
        desconto: 0,
        dataQuitacao: new Date().toISOString().split('T')[0],
        idConta: null,
        pagamentos: [],
      },
      fieldsTable: [
        { key: 'dataVencimento', label: 'Vencimento' },
        { key: 'descricao', label: 'Descrição' },
        { key: 'valorTotal', label: 'Valor Nominal' },
      ],
    }
  },
  computed: {
    somaPagamentos() {
      return this.dqModal.pagamentos.reduce((acc, p) => acc + Number(p.valor), 0)
    },
  },
  watch: {
    selectedRows: {
      immediate: true,
      handler(rows) {
        if (rows && rows.length > 0) {
          this.initDQ()
        }
      },
    },
  },
  methods: {
    initDQ() {
      const firstRow = this.selectedRows[0]
      this.dqModal.totalNominal = this.selectedRows.reduce((acc, r) => acc + (r.valorTotal - (r.valorQuitado || 0)), 0)
      this.dqModal.totalEncargos = 0
      this.dqModal.desconto = 0
      this.dqModal.dataQuitacao = new Date().toISOString().split('T')[0]
      this.dqModal.idConta = firstRow.conta ? (firstRow.conta.id || firstRow.conta) : null

      this.dqModal.pagamentos = [
        {
          valor: this.dqModal.totalNominal,
          idFormaPagamento: firstRow.formaPagamento ? (firstRow.formaPagamento.id || firstRow.formaPagamento) : null,
        },
      ]
      this.atualizarTotaisDQ()
    },
    atualizarTotaisDQ() {
      this.dqModal.totalGeral = this.dqModal.totalNominal + this.dqModal.totalEncargos - this.dqModal.desconto
      if (this.dqModal.pagamentos.length === 1) {
        this.dqModal.pagamentos[0].valor = this.dqModal.totalGeral
      }
    },
    addPagamentoDQ() {
      this.dqModal.pagamentos.push({ valor: 0, idFormaPagamento: null })
    },
    removePagamentoDQ(index) {
      this.dqModal.pagamentos.splice(index, 1)
    },
    async confirmarDQ() {
      if (Math.abs(this.somaPagamentos - this.dqModal.totalGeral) > 0.01) {
        this.MensagemError('A soma dos pagamentos diverge do total geral!')
        return
      }

      const payload = {
        idsItens: this.selectedRows.map(row => row.id),
        dataQuitacao: this.dqModal.dataQuitacao,
        desconto: this.dqModal.desconto,
        pagamentos: this.dqModal.pagamentos.map(p => ({
          valor: p.valor,
          formaPagamento: { id: p.idFormaPagamento },
        })),
        conta: { id: this.dqModal.idConta },
      }

      try {
        const result = await this.$store.dispatch('pagamentoRecebimentoFinancialModule/createGroupedQuittance', payload)
        if (result && result.error) {
          this.MensagemError('Erro na Quitação', result.message)
          return
        }
        this.$emit('success')
        this.$bvModal.hide(this.id)
      } catch (e) {
        this.MensagemError('Erro sistêmico', e.message)
      }
    },
  },
}
</script>
